import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import { useCountdown } from '../../../hooks/useCountdown';
import { usePolling } from '../../../hooks/usePolling';
import { DuitNowApprovalPendingScreen } from './DuitNowApprovalPendingScreen';
import ImportantNotesDialog from '../../register/RegisterScreen/components/ImportantNotesDialog';
import { SelectCardBrandStep } from '../../register/RegisterScreen/components/SelectCardBrandStep';
import { SelectDuitNowBankStep } from '../../register/RegisterScreen/components/SelectDuitNowBankStep';
import { SelectDuitNowIdStep } from '../../register/RegisterScreen/components/SelectDuitNowIdStep';
import { SelectDuitNowMethodStep } from '../../register/RegisterScreen/components/SelectDuitNowMethodStep';
import { SelectEWalletBrandStep } from '../../register/RegisterScreen/components/SelectEWalletBrandStep';
import { SelectPaymentTypeStep } from '../../register/RegisterScreen/components/SelectPaymentTypeStep';
import type { AddPaymentFlowHandlers } from '../hooks/useAddPaymentFlow';
import { useApprovalEvents } from '../utils/duitnow-approval-events';
import { createDuitNowApprovalPoll } from '../utils/duitnow-approval-poll';

interface AddPaymentMethodDialogProps extends AddPaymentFlowHandlers {
  open: boolean;
  onClose: () => void;
  isEWalletEnabled: boolean;
  isDuitNowEnabled: boolean;
  /** When false the DuitNow method screen is skipped, so Back on the bank step returns to
   * the payment-type list instead. */
  isDuitNowIdEnabled: boolean;
  /** Re-fetch the payment-methods list — called whenever a consent is created, and
   * whenever the polled row reaches a terminal state (ACTIVE/declined/not-found). */
  refreshPaymentMethods: () => void;
  /** Bumped by the page on `visibilitychange` while Screen 4 is open, to poll immediately
   * instead of waiting out the rest of the 3s tick. */
  pollPulse: number;
}

interface DialogDuitNowApprovalProps {
  consentRef: string;
  source: 'fresh' | 'resumed';
  pulse: number;
  /** The Radix Dialog's own effective `open` state (`open && !showImportantNotes`). Checked
   * synchronously (see `openRef` below), NOT just via unmount — Radix's exit animation
   * (`data-[state=closed]:animate-out duration-200`) can keep this component mounted for
   * ~200ms after the user closes, via its internal Presence bookkeeping. */
  dialogOpen: boolean;
  onActive: () => void;
  onFailed: () => void;
  onNotFound: () => void;
  onDone: () => void;
  /** Filled with this view's close reporter while mounted, so the dialog's close handler can
   * report the customer's close before the parent removes this view. */
  closeRef: React.MutableRefObject<(() => void) | null>;
}

function DialogDuitNowApproval({
  consentRef,
  source,
  pulse,
  dialogOpen,
  onActive,
  onFailed,
  onNotFound,
  onDone,
  closeRef
}: Readonly<DialogDuitNowApprovalProps>) {
  const [round, setRound] = useState(0);
  const { label, done, reset } = useCountdown(300, true);
  // Only a row we've actually observed PENDING and then lost counts as "declined". A row
  // we've never seen (stale/foreign ?duitnow_ref= link, or a race on the very first poll) is
  // unresolved, not declined. This persists across "Check again" rounds within the same mount
  // (a fresh mount — a new consentRef — starts it over, which is correct: it's a new request).
  const seenPendingRef = useRef(false);

  // `usePolling`'s cleanup (and this component's own unmount) only stops FUTURE ticks — a poll
  // already awaiting the list when the user closes is not cancelled by either. Two guards:
  //  - openRef: assigned during RENDER (not inside an effect), so it reflects a close the
  //    instant React re-renders with the new `open` value — no gap for a pending microtask
  //    (the in-flight fetch's continuation) to slip through before an effect runs. This is what
  //    catches Radix's ~200ms exit-animation window, where this component can still be mounted
  //    (and its useEffect not yet cleaned up) even though the dialog has visually started
  //    closing.
  //  - mountedRef: the fallback for a genuine unmount not caused by a close (e.g. navigating
  //    away entirely). Re-arms on every mount, including a StrictMode double-invoke.
  const openRef = useRef(dialogOpen);
  openRef.current = dialogOpen;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isActive = () => mountedRef.current && openRef.current;

  const events = useApprovalEvents({
    ref: consentRef,
    source,
    open: dialogOpen,
    timedOut: done
  });
  useEffect(() => {
    const close = events.close;
    closeRef.current = close;
    return () => {
      if (closeRef.current === close) closeRef.current = null;
    };
  }, [closeRef, events.close]);
  const poll = createDuitNowApprovalPoll({
    consentRef,
    source,
    isActive,
    seenPendingRef,
    onActive: () => {
      events.stopPoll('active');
      onActive();
    },
    onFailed: () => {
      events.stopPoll('failed');
      onFailed();
    },
    onNotFound: () => {
      events.stopPoll('not_found');
      onNotFound();
    },
    onStatus: events.setLastStatus
  });

  usePolling(
    () => {
      events.countPoll();
      return poll();
    },
    3000,
    !done,
    pulse,
    true // poll once immediately on mount (and again each "Check again") — an already-ACTIVE
    // ref (e.g. resumed via ?duitnow_ref=) shouldn't show a 3s "waiting" flash first.
  );
  return (
    <DuitNowApprovalPendingScreen
      key={round}
      compact
      countdownLabel={label}
      timedOut={done}
      onCheckAgain={() => {
        events.checkAgain();
        reset();
        setRound((r) => r + 1);
      }}
      onDone={onDone}
    />
  );
}

const AddPaymentMethodDialog: React.FC<AddPaymentMethodDialogProps> = ({
  open,
  onClose,
  isEWalletEnabled,
  isDuitNowEnabled,
  isDuitNowIdEnabled,
  refreshPaymentMethods,
  pollPulse,
  step,
  selectedBrand,
  showImportantNotes,
  isRedirecting,
  isResolvingGateway,
  errorMessage,
  pendingConsentRef,
  pendingConsentSource,
  lastDuitNowFlow,
  duitNowStepError,
  isSubmittingDuitNowStep,
  lastDuitNowBankCode,
  goToTypeSelection,
  // goToCardBrandSelection and goToEWalletBrandSelection are used via onSelectCards/onSelectEWallets
  goToCardBrandSelection,
  goToEWalletBrandSelection,
  goToDuitNowMethodSelection,
  confirmCardBrand,
  confirmEWalletBrand,
  confirmDuitNowMethod,
  confirmDuitNowBank,
  rememberDuitNowBank,
  confirmDuitNowId,
  closeImportantNotes,
  doAddPayment,
  setErrorMessage
}) => {
  const [, setSearchParams] = useSearchParams();
  // The parent may tear the approval view down in the same render that closes the dialog, so
  // the view's close is reported here, synchronously, before `onClose` runs.
  const approvalCloseRef = useRef<(() => void) | null>(null);
  const handleClose = () => {
    approvalCloseRef.current?.();
    onClose();
  };

  const selectedCardBrand =
    selectedBrand &&
    ['visa', 'mastercard', 'amex', 'unionpay'].includes(selectedBrand)
      ? (selectedBrand as Parameters<typeof confirmCardBrand>[0])
      : null;

  const selectedEWalletBrand =
    selectedBrand && ['grabpay', 'tng', 'shopeepay'].includes(selectedBrand)
      ? (selectedBrand as Parameters<typeof confirmEWalletBrand>[0])
      : null;

  return (
    <>
      <Dialog open={open && !showImportantNotes} onOpenChange={handleClose}>
        <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
          <DialogHeader>
            <DialogTitle className="sr-only">Add Payment Method</DialogTitle>
          </DialogHeader>

          {isRedirecting ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-[#5CB891] border-t-transparent" />
              <p className="text-sm font-medium text-gray-700">
                Redirecting...
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Please do not close or refresh the window.
              </p>
            </div>
          ) : errorMessage ? (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-red-600">{errorMessage}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 rounded-2xl bg-[#5CB891] px-6 py-3 text-sm font-semibold text-white hover:bg-[#4aa07c]"
              >
                Close
              </button>
            </div>
          ) : step === 'select-payment-type' ? (
            <SelectPaymentTypeStep
              onSelectCards={goToCardBrandSelection}
              onSelectEWallets={goToEWalletBrandSelection}
              onSelectDuitNow={goToDuitNowMethodSelection}
              onSkipPayment={onClose}
              onBack={onClose}
              isEWalletEnabled={isEWalletEnabled}
              isDuitNowEnabled={isDuitNowEnabled}
              backLabel={null}
              noTopMargin
            />
          ) : step === 'select-card-brand' ? (
            <SelectCardBrandStep
              initialBrand={selectedCardBrand}
              onConfirm={confirmCardBrand}
              onBack={goToTypeSelection}
              isLoading={isResolvingGateway}
              noTopMargin
            />
          ) : step === 'select-ewallet-brand' ? (
            <SelectEWalletBrandStep
              initialBrand={selectedEWalletBrand}
              onConfirm={confirmEWalletBrand}
              onBack={goToTypeSelection}
              isLoading={isResolvingGateway}
              noTopMargin
            />
          ) : step === 'select-duitnow-method' ? (
            <SelectDuitNowMethodStep
              // re-select the last choice when coming back from Screen 3 instead of
              // resetting to the default 'consent'.
              initial={lastDuitNowFlow}
              onConfirm={confirmDuitNowMethod}
              onBack={goToTypeSelection}
              isLoading={isRedirecting}
              noTopMargin
            />
          ) : step === 'select-duitnow-bank' ? (
            <SelectDuitNowBankStep
              initialBankCode={lastDuitNowBankCode}
              onConfirm={confirmDuitNowBank}
              onSelectionChange={rememberDuitNowBank}
              onBack={
                isDuitNowIdEnabled
                  ? goToDuitNowMethodSelection
                  : goToTypeSelection
              }
              backLabel={
                isDuitNowIdEnabled ? 'Authorization Method' : 'Payment Method'
              }
              isLoading={isSubmittingDuitNowStep}
              serverError={duitNowStepError}
              noTopMargin
            />
          ) : step === 'select-duitnow-id' ? (
            <SelectDuitNowIdStep
              onConfirm={confirmDuitNowId}
              onBack={goToDuitNowMethodSelection}
              isLoading={isSubmittingDuitNowStep}
              serverError={duitNowStepError}
              noTopMargin
            />
          ) : step === 'duitnow-approval' && pendingConsentRef ? (
            <DialogDuitNowApproval
              consentRef={pendingConsentRef}
              source={pendingConsentSource}
              pulse={pollPulse}
              dialogOpen={open && !showImportantNotes}
              onActive={() => {
                refreshPaymentMethods();
                // Replace, not push — a
                // poll-driven result must not leave a dead history entry.
                setSearchParams({ status: 'success' }, { replace: true });
              }}
              onFailed={() => {
                refreshPaymentMethods();
                setErrorMessage(
                  'The AutoDebit request was declined or cancelled in your banking app.'
                );
                setSearchParams({ status: 'failed' }, { replace: true });
              }}
              onNotFound={() => {
                refreshPaymentMethods();
                // Neutral, not a failure — nothing has actually failed for this ref.
                setErrorMessage(
                  "We couldn't find that AutoDebit request. Check your payment methods below.",
                  'neutral'
                );
                setSearchParams({ status: 'failed' }, { replace: true });
              }}
              onDone={handleClose}
              closeRef={approvalCloseRef}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ImportantNotesDialog
        open={showImportantNotes}
        onClose={closeImportantNotes}
        onComplete={doAddPayment}
      />
    </>
  );
};

export default AddPaymentMethodDialog;
